// Reading and building the wire tree itself: what a node is, how to address one,
// and how to make the shapes the editor needs. Nothing here knows about zones.
import {
  QueryFilter,
  QueryGroup,
  YukkaQuery,
} from 'src/app/shared/models/ai-search.interface';
import { DEFAULT_SLOP, QbCounts, QbNode } from './query-builder.types';

export function operatorOf(node: QbNode): 'and' | 'or' | null {
  const op = (node.operator ?? 'and').toLowerCase();
  return op === 'and' || op === 'or' ? op : null;
}

export function isLeaf(node: QbNode): boolean {
  return !(node.groups ?? []).length;
}

// A node that says nothing but "keep these out": every filter negated, nothing
// nested. Beside included terms it guards them; alone under an AND root it is
// the query-wide exclusions zone.
export function isExclusionsNode(node: QbNode): boolean {
  const filters = node.filters ?? [];
  return filters.length > 0 && filters.every((f) => f.negated) && isLeaf(node);
}

// --- Mutations: clone, resolve the path, mutate, return the new tree ---------

export function cloneTree<T extends QbNode>(tree: T): T {
  return JSON.parse(JSON.stringify(tree));
}

export function nodeAt(tree: QbNode, path: number[]): QbNode {
  let node: QbNode = tree;
  for (const i of path) node = node.groups[i];
  return node;
}

/**
 * A node that says nothing but "keep these out", however deeply: every filter
 * under it is negated, and there is at least one. Real queries wrap their
 * exclusion bags in an AND node, so the leaf test alone misses them.
 */
export function isExclusionSubtree(node: QbNode): boolean {
  const all: boolean[] = [];
  const walk = (n: QbNode): void => {
    for (const f of n.filters ?? []) all.push(!!f.negated);
    for (const child of n.groups ?? []) walk(child);
  };
  walk(node);
  return all.length > 0 && all.every(Boolean);
}

/** What a host should send: the tree without the empty groups editing leaves. */
export function prunedTree(tree: YukkaQuery): YukkaQuery {
  const next = cloneTree(tree);
  pruneEmpty(next);
  return next;
}

/** Drop nodes that hold nothing at all. The root always stays. */
export function pruneEmpty(node: QbNode): void {
  (node.groups ?? []).forEach((child) => pruneEmpty(child));
  node.groups = (node.groups ?? []).filter(
    (child) => (child.filters ?? []).length || (child.groups ?? []).length,
  );
}

// `name`/`input` are what the pill shows, `value` is what the query matches on.
// They differ for every field except free text, where the term is both.
export function makeFilter(
  value: string,
  negated = false,
  field = 'text',
  name?: string,
): QueryFilter {
  const label = name ?? value;
  return { field, value, negated, name: label, input: label };
}

export function makeTermsGroup(filters: QueryFilter[] = []): QueryGroup {
  return {
    operator: 'or',
    slop: DEFAULT_SLOP,
    filters: filters.map((f) => ({ ...f })),
    groups: [],
  };
}

// A term too broad on its own: it counts only alongside one of its context
// terms. The context group starts empty and the block reads as incomplete
// until it is filled.
export function makeGroundedGroup(term = ''): QueryGroup {
  return {
    operator: 'and',
    slop: DEFAULT_SLOP,
    filters: [makeFilter(term, false)],
    groups: [makeTermsGroup()],
  };
}

/**
 * A node's address as a string — the key terms and groups are tracked and
 * collapsed by. One definition, because it is a load-bearing convention: two
 * spellings of it and a picker opens against a key nothing else writes.
 */
export function pathKey(path: number[] | null | undefined): string {
  return (path ?? []).join('-');
}

// terms = included filters with a value; excluded = negated filters. Counted
// over the whole tree so the total never disagrees with what a re-run sends.
export function queryCounts(tree: QbNode | null | undefined): QbCounts {
  let terms = 0;
  let excluded = 0;
  const rec = (node: QbNode): void => {
    for (const f of node.filters ?? []) {
      if (f.negated) excluded++;
      else if ((f.value ?? '').trim()) terms++;
    }
    for (const g of node.groups ?? []) rec(g);
  };
  if (tree) rec(tree);
  return { terms, excluded };
}

// --- Serialization (preview) — tree-accurate, slop never rendered -----------

function serializeNode(node: QbNode): string {
  const op = ` ${(node.operator ?? 'and').toUpperCase()} `;
  const items: string[] = [];
  for (const f of node.filters ?? []) {
    if (!(f.value ?? '').trim()) continue;
    items.push(`${f.negated ? 'NOT ' : ''}${f.field ?? 'text'}:"${f.value}"`);
  }
  for (const g of node.groups ?? []) {
    const inner = serializeNode(g);
    if (inner) items.push(inner);
  }
  if (!items.length) return '';
  if (items.length === 1) return items[0];
  return `(${items.join(op)})`;
}

export function serializeQuery(tree: QbNode | null | undefined): string {
  if (!tree) return '';
  return serializeNode(tree);
}
